export const renderToCanvas = (
    canvas,
    background,
    textBlocks,
    rotation = 0,
    flipX = false,
    flipY = false
) => {
    return new Promise((resolve, reject) => {
        if (!canvas || !background) {
            reject(new Error('Canvas or background not provided'))
            return
        }

        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
            try {
                canvas.width = img.width
                canvas.height = img.height

                ctx.clearRect(0, 0, canvas.width, canvas.height)

                ctx.save()

                ctx.translate(img.width / 2, img.height / 2)

                if (rotation !== 0) {
                    ctx.rotate((rotation * Math.PI) / 180)
                }

                if (flipX || flipY) {
                    ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1)
                }

                ctx.drawImage(img, -img.width / 2, -img.height / 2)

                ctx.restore()

                textBlocks.forEach(block => {
                    ctx.save()

                    const fontStyle = block.fontStyle || (block.fontWeight === 'italic' ? 'italic' : 'normal')
                    const fontWeight = block.fontWeight === 'bold' ? 'bold' : 'normal'
                    ctx.font = `${fontStyle} ${fontWeight} ${block.fontSize}px ${block.fontFamily}`
                    ctx.fillStyle = block.color
                    ctx.textAlign = block.textAlign || 'left'
                    ctx.textBaseline = 'top'

                    const text = block.content || block.text || ''
                    const x = block.x || 0
                    const y = block.y || 0

                    if (block.width && block.width !== 'auto') {
                        const words = text.split(' ')
                        let line = ''
                        let lineHeight = block.fontSize * 1.2
                        let currentY = y

                        for (let i = 0; i < words.length; i++) {
                            const testLine = line + words[i] + ' '
                            const metrics = ctx.measureText(testLine)

                            if (metrics.width > block.width && i > 0) {
                                ctx.fillText(line, x, currentY)
                                line = words[i] + ' '
                                currentY += lineHeight
                            } else {
                                line = testLine
                            }
                        }

                        if (line) {
                            ctx.fillText(line, x, currentY)
                        }
                    } else {
                        ctx.fillText(text, x, y)
                    }

                    ctx.restore()
                })

                resolve(canvas)
            } catch (error) {
                reject(error)
            }
        }

        img.onerror = () => {
            reject(new Error('Failed to load background image'))
        }

        if (background.imageData && background.imageData.startsWith('data:')) {
            img.src = background.imageData
        } else if (background.imageUrl) {
            img.src = background.imageUrl
        } else {
            reject(new Error('No valid image source found'))
        }
    })
}

export const generateCurrentBackgroundPostcardsCanvas = async (
    background,
    textBlocks,
    csvData,
    csvFileNameColumn = null,
    options = {}
) => {
    if (!background || !csvData || csvData.length === 0) {
        return []
    }

    const { quality = 1.0 } = options
    const result = []

    for (let rowIndex = 0; rowIndex < csvData.length; rowIndex++) {
        const csvRow = csvData[rowIndex]

        let fileName = `postcard_${rowIndex + 1}`
        if (csvFileNameColumn && csvRow[csvFileNameColumn]) {
            const columnValue = String(csvRow[csvFileNameColumn])
            fileName = columnValue
                .replace(/[^\w\u0400-\u04FF\s\-]/gi, '_')
                .replace(/\s+/g, '_')
                .trim()

            if (!fileName) {
                fileName = `postcard_${rowIndex + 1}`
            }
        }

        const processedTextBlocks = textBlocks.map(block => {
            const processedBlock = { ...block }

            if (block.key && block.text) {
                let processedText = block.text

                const regex = /\{(\w+)\}/g
                let match

                while ((match = regex.exec(block.text)) !== null) {
                    const placeholderKey = match[1]
                    const value = csvRow[placeholderKey]

                    if (value !== undefined && value !== null) {
                        const placeholderRegex = new RegExp(`\\{${escapeRegExp(placeholderKey)}\\}`, 'g')
                        processedText = processedText.replace(placeholderRegex, String(value))
                    }
                }

                processedBlock.content = processedText
                processedBlock.text = processedText
            } else {
                processedBlock.content = block.text || ''
                processedBlock.text = block.text || ''
            }

            return processedBlock
        })

        result.push({
            id: `${background.id}_${rowIndex}`,
            background,
            csvRow,
            textBlocks: processedTextBlocks,
            fileName: `${fileName}.png`,
            rowIndex,
        })
    }

    return result
}

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const generateAllPostcardsCanvas = async (
    backgrounds,
    textBlocks,
    csvData,
    csvFileNameColumn = null,
    options = {}
) => {
    if (!backgrounds || backgrounds.length === 0 || !csvData || csvData.length === 0) {
        return []
    }

    const { quality = 1.0 } = options
    const result = []

    for (let bgIndex = 0; bgIndex < backgrounds.length; bgIndex++) {
        const background = backgrounds[bgIndex]

        for (let rowIndex = 0; rowIndex < csvData.length; rowIndex++) {
            const csvRow = csvData[rowIndex]

            let fileName = `postcard_${bgIndex + 1}_${rowIndex + 1}`
            if (csvFileNameColumn && csvRow[csvFileNameColumn]) {
                const columnValue = String(csvRow[csvFileNameColumn])
                fileName = columnValue
                    .replace(/[^\w\u0400-\u04FF\s\-]/gi, '_')
                    .replace(/\s+/g, '_')
                    .trim()

                if (!fileName) {
                    fileName = `postcard_${bgIndex + 1}_${rowIndex + 1}`
                }
            }

            const processedTextBlocks = textBlocks.map(block => {
                const processedBlock = { ...block }

                if (block.key && block.text) {
                    const value = csvRow[block.key]
                    if (value !== undefined && value !== null) {
                        const regex = new RegExp(`\\{${escapeRegExp(block.key)}\\}`, 'g')
                        processedBlock.content = block.text.replace(regex, String(value))
                        processedBlock.text = processedBlock.content
                    } else {
                        processedBlock.content = block.text
                        processedBlock.text = block.text
                    }
                } else {
                    processedBlock.content = block.text || ''
                    processedBlock.text = block.text || ''
                }

                return processedBlock
            })

            result.push({
                id: `${background.id}_${rowIndex}`,
                background,
                csvRow,
                textBlocks: processedTextBlocks,
                fileName: `${fileName}.png`,
                bgIndex,
                rowIndex,
            })
        }
    }

    return result
}

export const canvasToDataURL = (canvas, format = 'png', quality = 1.0) => {
    if (!canvas) return ''

    const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
    return canvas.toDataURL(mimeType, format === 'jpeg' ? quality : undefined)
}

export const exportCanvasToBlob = (canvas, format = 'png', quality = 1.0) => {
    return new Promise((resolve) => {
        if (!canvas) {
            resolve(null)
            return
        }

        canvas.toBlob(
            (blob) => resolve(blob),
            format === 'png' ? 'image/png' : 'image/jpeg',
            format === 'jpeg' ? quality : 1.0
        )
    })
}